import { cloneDeep, isEmpty, uniqWith } from 'lodash-es';
import {
  IAdditionalPaymentDetails,
  IBooking,
  IBookingMenuOptionExtrasUpdater,
  ISavedBookingSelectedOptions,
  IServicePaymentDetails,
  IMenuOption,
  PricingTypes,
  servicePaymentType,
  ISavedBookingMenuOption,
  ISavedExtrasOption,
  IBookingMenuOptionExtras,
  IBookingMenuOption,
  IOption,
  IMenuOptionsResponse,
  BookingMethods,
  IPaymentDetailsBase,
  ITableDate,
  ISimpleBookingOption,
  IPouchServicePaymentOption,
} from 'shared-types/index';
import BookingService from 'external-services/booking-service';
import { isEqual } from 'lodash-es';
import { AxiosInstance } from 'axios';
import NetworkRequestsService from 'external-services/network-requests-service/index';
import AdminService from 'external-services/diary-admin-service/admin.service';

const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();

const NS = 'BookingUpsellOptionsService';

class BookingUpsellOptionsService {
  /**
   * Transforms menu options into a flat list, extracting child items from `extras` and
   * pushing them into an flat array property for backend to ingest.
   * Expects list of `options` matching `bookingService.booking.selectedMenuOptions` schema.
   */
  getFlatExtras(options: IBookingMenuOption[]): ISavedBookingMenuOption[] {
    const menuOptionsWithFlatExtras: ISavedBookingMenuOption[] = options.reduce(
      (acc1, originalOption: IBookingMenuOption) => {
        /**
         * here we merge `option.extras` data into a single `extras` property for back end to ingest
         **/
        const copiedOption: ISavedBookingMenuOption = {
          menuOptionId: originalOption.menuOptionId,
          quantity: originalOption.quantity,
          isUpsellItem: originalOption.isUpsellItem || false,
        };

        if (!isEmpty(originalOption.extras)) {
          const extras = cloneDeep(originalOption.extras);
          const isSameForAll = extras.isSameForAll;

          if (!isSameForAll) {
            copiedOption.quantity = 1;
          }

          const explicitChildMenuOptions: ISavedExtrasOption[] = (
            extras.explicitChildMenuOptions || []
          ).reduce((acc2: ISavedExtrasOption[], _optionsList: IBookingMenuOption[], i: number) => {
            const optionsList: ISavedExtrasOption[] = _optionsList.map(
              ({ menuOptionId, quantity }) => {
                return { menuOptionId, quantity, isExplicit: true };
              }
            );

            if (i === 0 || isSameForAll) {
              /**
               * If same for all, then the quantity is defined on the parent and all should be 1.
               * Note: if checkboxes become spinners (at some point) this will have to change to have
               * custom quantities.
               */
              if (isSameForAll) {
                optionsList.forEach((o) => {
                  o.quantity = 1;
                });
              }
              acc2 = acc2.concat(optionsList);
            } else {
              /**
               * Here we clone the parent option for each extra quantity and pass the individual extras to it
               */
              const copiedParentOption: ISavedBookingMenuOption = {
                menuOptionId: copiedOption.menuOptionId,
                quantity: 1,
                extras: optionsList,
              };
              acc1.push(copiedParentOption);
            }
            return acc2;
          }, []);

          const implicitChildMenuOptions: ISavedExtrasOption[] = (
            extras.implicitChildMenuOptions || []
          ).map(({ menuOptionId, quantity }) => {
            return { menuOptionId, quantity, isExplicit: false };
          });

          copiedOption.extras = implicitChildMenuOptions.concat(explicitChildMenuOptions);
        }
        acc1.push(copiedOption);

        return acc1;
      },
      []
    );

    return this.combineEmptyExtras(menuOptionsWithFlatExtras);
  }

  getChildBookingOptionsIds(options: any) {
    const hasChildItems =
      options.childMenuOptionIds?.length || options.explicitChildMenuOptionIds?.length;
    if (hasChildItems) {
      return [...options.childMenuOptionIds, ...options.explicitChildMenuOptionIds];
    } else {
      return null;
    }
  }

  getEmptyExtrasMenuOption(): IBookingMenuOptionExtras {
    return {
      explicitChildMenuOptions: [],
      implicitChildMenuOptions: [],
      isSameForAll: true,
    };
  }

  /**
   * Finds parent options with empty extras and combines them
   */
  private combineEmptyExtras(options: ISavedBookingMenuOption[]): ISavedBookingMenuOption[] {
    return options.reduce(
      (acc: ISavedBookingMenuOption[], opt: ISavedBookingMenuOption, i: number) => {
        if (opt.extras && !opt.extras.length) {
          const existingSameEmptyOption: ISavedBookingMenuOption = acc.find(
            ({ menuOptionId, extras }) =>
              opt.menuOptionId === menuOptionId && extras && !extras.length
          );
          if (existingSameEmptyOption) {
            existingSameEmptyOption.quantity += opt.quantity;
          } else {
            acc.push(opt);
          }
        } else {
          acc.push(opt);
        }

        return acc;
      },
      []
    );
  }

  getSelectedMenuOptionFromId(
    menuOptionId: string,
    paymentDetails: IPaymentDetailsBase,
    maxCovers: number
  ) {
    return BookingService.getBookingOptions(
      paymentDetails.options,
      [{ menuOptionId, quantity: 1, isUpsellItem: false }],
      maxCovers,
      paymentDetails.singleMenuPerBooking
    );
  }

  // updateMenuOptionExtras(data: IBookingMenuOptionExtrasUpdater, serviceId: string, bookingDate: Date, bookingTimes: ITableDate[]) {
  //   return new Promise(resolve => {
  //
  //     const state = MenuOptionReducerService.getState();
  //     const {parentId, isSameForAll, explicitChildMenuOptions, implicitChildMenuOptions} = data;
  //     /**
  //      * @param {[type]}
  //      */
  //     if (data.parentQuantity > 0) {
  //       const parentBookingOption = state.booking.selectedMenuOptions.find((val: IBookingMenuOption) => val.menuOptionId === parentId);
  //       const {quantity, menuOptionId} = parentBookingOption;
  //       const bookingOption: IBookingMenuOption = {
  //         quantity, menuOptionId,
  //         extras: {
  //           isSameForAll, explicitChildMenuOptions, implicitChildMenuOptions
  //         }
  //       };
  //       const opts: IBookingMenuOption[] = [bookingOption];
  //       PaymentService.getPaymentTypesPerBookingOption(opts, serviceId, bookingDate, bookingTimes, state.booking.people, true)
  //         .pipe(
  //           first(),
  //           map(({data}) => data.paymentTypeName)) //@todo: check why we are using paymentTypeName instead of paymentType
  //         .subscribe((paymentType: servicePaymentType) => {
  //           const payload = {
  //             paymentTypeOverride: {paymentType, hasError: false},
  //             id: data.parentId
  //           }
  //           MenuOptionReducerService.updateBookingOptionPayment(payload);
  //           MenuOptionReducerService.updateMenuOptionExtras(data);
  //           resolve(null);
  //         }, (err: any) => {
  //           console.log('paymentType err', err);
  //           const payload: any = {
  //             paymentTypeOverride: {paymentType: null, hasError: false},
  //             id: data.parentId
  //           }
  //           MenuOptionReducerService.updateBookingOptionPayment(payload);
  //           MenuOptionReducerService.updateMenuOptionExtras(data);
  //           resolve(null);
  //         });
  //     }
  //
  //     // const payload = data;
  //     // MenuOptionReducerService.updateMenuOptionExtras(payload);
  //     // resolve();
  //
  //   });
  // }

  getMenuOptionDetailsFromIds(menuOptions: string[], allBookingOptions: any[]) {
    if (isEmpty(allBookingOptions)) {
      return [];
    }
    return (menuOptions || []).map(
      (id) =>
        Object.assign(
          allBookingOptions.find((option) => option._id === id),
          { id: id }
        ) // adding a object id in MenuOption to has the same type like ISimpleBookingOption
    );
  }

  updateSelectedMenuOption(selectedMenuOptions: IBookingMenuOption[]) {
    return new Promise((resolve) => {
      // const payload = {selectedMenuOptions}
      // MenuOptionReducerService.updateSelectedMenuOption(selectedMenuOptions);
      resolve(null);
    });
  }

  checkForToggleStatus(
    isUpsell: boolean,
    menuOptions: ISavedBookingSelectedOptions[],
    optionIds: string[]
  ): boolean {
    if (isEmpty(optionIds)) {
      return false;
    }

    const selectedOptions =
      menuOptions?.length &&
      menuOptions.filter(
        (option) => option.isUpsellItem === isUpsell && optionIds.includes(option.id)
      );
    return selectedOptions?.length > 0;
  }

  updateSelectedUpsellMenuOption(selectedMenuOptions: IBookingMenuOption[]) {
    return new Promise((resolve) => {
      // const payload = {selectedMenuOptions}
      // MenuOptionReducerService.updateSelectedUpsellMenuOption(selectedMenuOptions);
      resolve(null);
    });
  }

  /**
   * Populates the isUpsellItem on ISavedBookingSelectedOptions
   */
  getSelectedOptionsWithUpsell(
    option: ISavedBookingSelectedOptions,
    selectedMenuOptions: IBookingMenuOption[]
  ): ISavedBookingSelectedOptions {
    selectedMenuOptions.some((t) => {
      if (t.menuOptionId === option.id) {
        option.isUpsellItem = t.isUpsellItem;
        return true;
      }
      return false;
    });
    return option;
  }

  /**
   * Returns true if any options exist with qty more than zero.
   * @param isUpsell - if options are for upsell BOs or not
   * @param selectedOptions - when editing straight from a widget booking this will be populated & selectedMenuOptions will not
   * @param selectedMenuOptions - once saved
   */
  hasBookingOptions(
    isUpsell: boolean,
    selectedOptions: { quantity: number; isUpsellItem: boolean }[],
    selectedMenuOptions: { quantity: number; isUpsellItem: boolean }[]
  ): boolean {
    if (isUpsell) {
      if (selectedOptions?.length) {
        return !!this.filterBookingOptionsUpsell(selectedOptions).length;
      }
      return !!this.filterBookingOptionsUpsell(selectedMenuOptions).length;
    } else {
      if (selectedOptions?.length) {
        return !!this.filterBookingOptionsNonUpsell(selectedOptions).length;
      }
      return !!this.filterBookingOptionsNonUpsell(selectedMenuOptions).length;
    }
  }

  filterBookingOptionsNonUpsell(
    options: { quantity: number; isUpsellItem: boolean }[]
  ): { quantity: number; isUpsellItem: boolean }[] {
    if (!options) return [];
    return options.filter((t) => !t.isUpsellItem && t.quantity);
  }

  filterBookingOptionsUpsell(
    options: { quantity: number; isUpsellItem: boolean }[]
  ): { quantity: number; isUpsellItem: boolean }[] {
    if (!options) return [];
    return options.filter((t) => t.isUpsellItem && t.quantity);
  }

  showManageBookingOptions(
    isUpsell: boolean,
    bookingMethod: BookingMethods,
    isTakeAwayMode: boolean,
    currentPax: number,
    activePaymentDetails: IAdditionalPaymentDetails,
    originalPaymentDetails: IAdditionalPaymentDetails,
    menuBookingOptions: IOption[],
    menuUpsellOptions: IOption[]
  ): boolean {
    let isPaidBOPresent = false;
    let showButtonBasedOnMenuOptions = false;

    // IF VENUS IS TakeAwayMode => DON'T SHOW THE MANAGE BOOKING OPTIONS BUTTON
    if (isTakeAwayMode && bookingMethod !== BookingMethods.phone) {
      return false;
    } else if (activePaymentDetails) {
      const originalPaymentDetailsHasPayment =
        originalPaymentDetails &&
        originalPaymentDetails.paymentType !== servicePaymentType.noPayment;

      const nonUpsellBOs = activePaymentDetails.menuOptions;
      const isBo =
        nonUpsellBOs &&
        currentPax >= activePaymentDetails.peopleRequired &&
        nonUpsellBOs.length > 0;
      const isUo = menuUpsellOptions.length > 0;
      showButtonBasedOnMenuOptions = isUpsell ? isUo : isBo;

      if (showButtonBasedOnMenuOptions && originalPaymentDetailsHasPayment) {
        isPaidBOPresent = this.checkForPaidBO(isUpsell ? menuUpsellOptions : menuBookingOptions);
        return !isPaidBOPresent;
      }
      return !!(
        originalPaymentDetails &&
        originalPaymentDetails.paymentType === servicePaymentType.noPayment &&
        showButtonBasedOnMenuOptions
      );
    }
    return false;
  }

  setBOPaxWhenSingleMenuPerBooking(
    selectedOptions: IBookingMenuOption[],
    paymentDetails: IServicePaymentDetails,
    currentPax: number
  ): void {
    if (paymentDetails.singleMenuPerBooking && paymentDetails.pricingType === PricingTypes.PerPax) {
      selectedOptions.forEach((opt) => {
        if (!opt.isUpsellItem) {
          // upsell options can be any qty, but BO must match pax
          opt.quantity = currentPax;
        }
      });
    }
  }

  // combines new with existing and runs equality check on objects to prevent duplicates
  combineBookingOptions(
    newBOs: IBookingMenuOption[],
    existingBOs: IBookingMenuOption[],
    newIsUpsell: boolean
  ): IBookingMenuOption[] {
    const _existingBOs: IBookingMenuOption[] = existingBOs.filter((bo) =>
      newIsUpsell ? !bo.isUpsellItem : bo.isUpsellItem
    );

    return uniqWith(newBOs.concat(_existingBOs), (value, other) => {
      // checks the equality of the ids and upsell flag, but qty is taken from the newer 'booking.selectedMenuOptions'
      return value.menuOptionId === other.menuOptionId && value.isUpsellItem === other.isUpsellItem;
    });
  }

  /**
   * Checks if child options have payments on them.
   * Worth noting that editBooking.controller sets 'childOptions' for non-upsell BOs only.
   */
  private checkForPaidBO(menuOptions: IOption[]): boolean {
    return menuOptions.some((options) => {
      if (options.price > 0) {
        return true;
      } else if (!isEmpty(options.childOptions) && options.childOptions.some((o) => o.price > 0)) {
        return true;
      }
      return false;
    });
  }

  checkIfOptionsChanged(newBOIds: string[], oldBOIds: string[]): boolean {
    return !isEqual(newBOIds?.sort(), oldBOIds?.sort());
  }

  filterBasedOnUpsellItem(booking: IBooking, isUpsell: Boolean) {
    const selectedMenuOptions = booking.selectedMenuOptions.filter(
      (option) => option.isUpsellItem === isUpsell
    );
    const selectedOptions = booking.selectedOptions.filter(
      (option) => option.isUpsellItem === isUpsell
    );
    return { ...booking, selectedMenuOptions, selectedOptions };
  }

  updateSelectedOptions(
    newSelectedMenuOptions: ISavedBookingMenuOption[],
    allBookingOptions: IPouchServicePaymentOption[]
  ): ISavedBookingSelectedOptions[] {
    const options: ISavedBookingSelectedOptions[] = (newSelectedMenuOptions || []).map(
      (selectedMenuOption: ISavedBookingMenuOption) => {
        if (selectedMenuOption.extras && selectedMenuOption.extras.length > 0) {
          selectedMenuOption.extras = selectedMenuOption.extras.map((extra: ISavedExtrasOption) => {
            return this.buildMenuOptionExtraObject(
              this.searchFromAllOptions(allBookingOptions, extra.menuOptionId),
              extra
            );
          });
        }
        return this.buildMenuOptionParentObject(
          this.searchFromAllOptions(allBookingOptions, selectedMenuOption.menuOptionId),
          selectedMenuOption
        );
      }
    );
    return options;
  }

  private buildMenuOptionParentObject(
    object: IPouchServicePaymentOption,
    opt: ISavedBookingMenuOption
  ): ISavedBookingSelectedOptions {
    const { name, label, price, paymentType, tag } = object;
    return {
      id: opt.menuOptionId,
      name,
      label,
      price,
      paymentType,
      quantity: opt.quantity,
      extras: opt.extras || [],
      tag,
      isUpsellItem: opt.isUpsellItem,
    };
  }

  private buildMenuOptionExtraObject(
    extra: IPouchServicePaymentOption,
    opt: ISavedExtrasOption
  ): ISavedExtrasOption {
    const temp: any = {};
    temp.name = extra.name;
    temp.label = extra.label;
    temp.price = extra.price;
    temp.paymentType = extra.paymentType;
    temp.tag = extra.tag;

    temp.id = opt.menuOptionId;
    temp.quantity = opt.quantity;
    temp.isExplicit = opt.isExplicit;
    return temp as ISavedExtrasOption;
  }

  private searchFromAllOptions(
    allBookingOptions: IPouchServicePaymentOption[],
    menuOptionId: string
  ): IPouchServicePaymentOption {
    return allBookingOptions.find((option) => option._id === menuOptionId);
  }

  public getBookingOptionsFromAPI(childMenuOptionIds: string): Promise<IMenuOptionsResponse> {
    // this is a promise (async)
    return api.post(
      AdminService.getBookingOptionsEndpointsURL(),
      { childMenuOptionIds }
    );
  }
}
const instance = new BookingUpsellOptionsService();
export default instance as BookingUpsellOptionsService;
